import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import B2b from '../single-page/api/b2b';
import { ROUTES } from '../single-page/constants/routes';
import { brandTokens } from '../single-page/theme';
import PackageCard from '../single-page/components/PackageCard';
import { useUser } from '../hooks/useUser';
import { useModals } from '../hooks/useModals';

const PENDING_PACKAGE_KEY = 'b2b_pending_package';

const SectionRoot = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: brandTokens.backgroundApp,
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(10, 4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(7, 2),
  },
}));

const Content = styled(Box)({
  width: '100%',
  maxWidth: '1100px',
  textAlign: 'center',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '32px',
  color: brandTokens.navy,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  color: 'rgba(1, 47, 71, 0.72)',
  marginBottom: theme.spacing(5),
}));

const CardsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const B2bPackagesSection = () => {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, loading: userLoading } = useUser();
  const { handleOpenLogin } = useModals();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const isCompany = Boolean(currentUser?.company_id || currentUser?.company);

  useEffect(() => {
    let mounted = true;
    B2b.getPackages()
      .then((data) => {
        if (mounted) setPackages(data?.packages || []);
      })
      .catch(() => {
        if (mounted) setPackages([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Retoma a compra do pacote escolhido antes do login
  useEffect(() => {
    if (userLoading || !isAuthenticated || !isCompany) return;
    const raw = sessionStorage.getItem(PENDING_PACKAGE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PENDING_PACKAGE_KEY);
    try {
      const pkg = JSON.parse(raw);
      navigate(ROUTES.PACKAGE_PAYMENT, { state: { package: pkg } });
    } catch {
      // intento corrompido: usuário pode clicar novamente
    }
  }, [userLoading, isAuthenticated, isCompany, navigate]);

  const handleBuy = (pkg) => {
    if (isCompany) {
      navigate(ROUTES.PACKAGE_PAYMENT, { state: { package: pkg } });
      return;
    }
    sessionStorage.setItem(PENDING_PACKAGE_KEY, JSON.stringify(pkg));
    handleOpenLogin();
  };

  if (loading) {
    return (
      <SectionRoot>
        <CircularProgress />
      </SectionRoot>
    );
  }

  if (!packages.length) return null;

  return (
    <SectionRoot>
      <Content>
        <SectionTitle component="h2">Pacotes para empresas</SectionTitle>
        <SectionSubtitle>
          Consultas em volume para frotas, lojistas e despachantes. Compre créditos e use quando precisar.
        </SectionSubtitle>
        <CardsGrid>
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} onBuy={handleBuy} ctaLabel="Comprar pacote" />
          ))}
        </CardsGrid>
      </Content>
    </SectionRoot>
  );
};

export default B2bPackagesSection;
